import axios from 'axios';
import { toastr } from 'react-redux-toastr';
import { createAction } from 'redux-act';
import { url } from 'utils/url';

export const AVAILABILITY_CHECK_REQUEST = createAction(
  'AVAILABILITY_CHECK_REQUEST'
);
export const AVAILABILITY_CHECK_SUCCESS = createAction(
  'AVAILABILITY_CHECK_SUCCESS'
);
export const AVAILABILITY_CHECK_FAILURE = createAction(
  'AVAILABILITY_CHECK_FAILURE'
);
export const AVAILABILITY_CLEAR = createAction('AVAILABILITY_CLEAR');

export const SLOT_CREATE_INIT = createAction('SLOT_CREATE_INIT');
export const SLOT_CREATE_SUCCESS = createAction('SLOT_CREATE_SUCCESS');
export const SLOT_CREATE_FAIL = createAction('SLOT_CREATE_FAIL');

export const SLOT_UPDATE_INIT = createAction('SLOT_UPDATE_INIT');
export const SLOT_UPDATE_SUCCESS = createAction('SLOT_UPDATE_SUCCESS');
export const SLOT_UPDATE_FAIL = createAction('SLOT_UPDATE_FAIL');

export const SLOT_DELETE_INIT = createAction('SLOT_DELETE_INIT');
export const SLOT_DELETE_SUCCESS = createAction('SLOT_DELETE_SUCCESS');
export const SLOT_DELETE_FAIL = createAction('SLOT_DELETE_FAIL');

export const SLOT_MEDIA_ASSIGN_INIT = createAction('SLOT_MEDIA_ASSIGN_INIT');
export const SLOT_MEDIA_ASSIGN_SUCCESS = createAction(
  'SLOT_MEDIA_ASSIGN_SUCCESS'
);
export const SLOT_MEDIA_ASSIGN_FAIL = createAction('SLOT_MEDIA_ASSIGN_FAIL');

export const SLOT_MEDIA_REMOVE_INIT = createAction('SLOT_MEDIA_REMOVE_INIT');
export const SLOT_MEDIA_REMOVE_SUCCESS = createAction(
  'SLOT_MEDIA_REMOVE_SUCCESS'
);
export const SLOT_MEDIA_REMOVE_FAIL = createAction('SLOT_MEDIA_REMOVE_FAIL');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
  'Content-Type': 'application/json',
});

const errMsg = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.details ||
  error?.message ||
  fallback;

export const fetchAvailability = (
  screenIds,
  startDate,
  endDate,
  startTime,
  endTime,
  isEditing,
  editingTemplateId
) => {
  return async (dispatch) => {
    const startStr =
      typeof startDate === 'string'
        ? startDate
        : startDate?.toISOString().split('T')[0];
    const endStr =
      typeof endDate === 'string'
        ? endDate
        : endDate?.toISOString().split('T')[0];

    if (
      !screenIds ||
      screenIds.length === 0 ||
      !startStr ||
      !endStr ||
      !startTime ||
      !endTime
    ) {
      dispatch(AVAILABILITY_CLEAR());
      return;
    }

    dispatch(AVAILABILITY_CHECK_REQUEST());

    try {
      const response = await axios.post(
        `${url}/slot/availability`,
        {
          screenIds,
          startDate: startStr,
          endDate: endStr,
          startTime,
          endTime,
          isEditing,
          editingTemplateId,
        },
        { headers: authHeaders() }
      );
      if (response.data && Array.isArray(response.data.data)) {
        dispatch(AVAILABILITY_CHECK_SUCCESS(response.data.data));
      } else {
        const text = 'Invalid response format from server.';
        toastr.error('Response Error', text);
        dispatch(AVAILABILITY_CHECK_FAILURE(text));
      }
    } catch (error) {
      const msg = errMsg(error, 'Failed to check availability.');
      toastr.error('Availability Check Failed', msg);
      dispatch(AVAILABILITY_CHECK_FAILURE(msg));
    }
  };
};

export const clearAvailabilityState = () => (dispatch) =>
  dispatch(AVAILABILITY_CLEAR());

export const createSlot = (payload, cb) => {
  return async (dispatch) => {
    dispatch(SLOT_CREATE_INIT());
    try {
      const response = await axios.post(`${url}/slot`, payload, {
        headers: authHeaders(),
      });
      const created = response.data.data;
      dispatch(SLOT_CREATE_SUCCESS({ slot: created }));
      if (cb) cb(created);
      return created;
    } catch (error) {
      const msg = errMsg(error, 'Failed to create slot');
      toastr.error('Error Creating Slot', msg);
      dispatch(SLOT_CREATE_FAIL({ error: msg }));
      throw error;
    }
  };
};

export const updateSlot = ({ id, ...fields }, cb) => {
  return async (dispatch) => {
    dispatch(SLOT_UPDATE_INIT());
    try {
      const response = await axios.patch(`${url}/slot/${id}`, fields, {
        headers: authHeaders(),
      });
      const updated = response.data.data;
      dispatch(SLOT_UPDATE_SUCCESS({ slot: updated, id }));
      if (cb) cb(updated);
      return updated;
    } catch (error) {
      const msg = errMsg(error, 'Failed to update slot');
      toastr.error('Error Updating Slot', msg);
      dispatch(SLOT_UPDATE_FAIL({ error: msg }));
      throw error;
    }
  };
};

export const deleteSlot = ({ id }, cb) => {
  return async (dispatch) => {
    dispatch(SLOT_DELETE_INIT());
    try {
      await axios.delete(`${url}/slot/${id}`, { headers: authHeaders() });
      dispatch(SLOT_DELETE_SUCCESS({ id }));
      if (cb) cb();
    } catch (error) {
      const msg = errMsg(error, 'Failed to delete slot');
      toastr.error('Error Deleting Slot', msg);
      dispatch(SLOT_DELETE_FAIL({ error: msg }));
      throw error;
    }
  };
};

export const assignMediaToSlot = ({ slotId, mediaIds }, cb) => {
  return async (dispatch) => {
    dispatch(SLOT_MEDIA_ASSIGN_INIT());
    try {
      const response = await axios.post(
        `${url}/slot/${slotId}/media`,
        { mediaIds },
        { headers: authHeaders() }
      );
      const updated = response.data.data;
      dispatch(SLOT_MEDIA_ASSIGN_SUCCESS({ slotId, slot: updated }));
      if (cb) cb(updated);
      return updated;
    } catch (error) {
      const msg = errMsg(error, 'Failed to assign media to slot');
      toastr.error('Error Assigning Media', msg);
      dispatch(SLOT_MEDIA_ASSIGN_FAIL({ error: msg }));
      throw error;
    }
  };
};

export const removeMediaFromSlot = ({ slotId, mediaId }, cb) => {
  return async (dispatch) => {
    dispatch(SLOT_MEDIA_REMOVE_INIT());
    try {
      await axios.delete(`${url}/slot/${slotId}/media/${mediaId}`, {
        headers: authHeaders(),
      });
      dispatch(SLOT_MEDIA_REMOVE_SUCCESS({ slotId, mediaId }));
      if (cb) cb();
    } catch (error) {
      const msg = errMsg(error, 'Failed to remove media from slot');
      toastr.error('Error Removing Media', msg);
      dispatch(SLOT_MEDIA_REMOVE_FAIL({ error: msg }));
      throw error;
    }
  };
};
